import React, { useEffect, useState } from 'react';
import {
  Box, List, ListItem, ListItemButton, ListItemText, Typography,
  Pagination, Stack, Select, MenuItem, FormControl, InputLabel, TextField, Chip,
  FormControlLabel, Switch, CircularProgress
} from '@mui/material';
import api from '../utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';

const PAGE_SIZE = 20;

function RecordedByTimePage() {
  // 篩選 / 分頁狀態存在 URL query，離開頁面再返回時自動還原（也可分享連結）。
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const stationFilter = searchParams.get('station') || '';
  const searchKeyword = searchParams.get('kw') || '';
  const selectedDate = searchParams.get('date') || '';
  const translatedOnly = searchParams.get('translated') === '1';

  // 關鍵字輸入框用本地 state 做即時輸入，debounce 後才寫進 URL / 觸發查詢。
  const [keywordInput, setKeywordInput] = useState(searchKeyword);

  const [programs, setPrograms] = useState([]);
  const [total, setTotal] = useState(0);
  const [stationList, setStationList] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // 更新 URL query 的小工具：合併既有參數，改篩選時把 page 歸 1。
  const updateParams = (patch, { resetPage = true } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === '' || v === false || v == null) next.delete(k);
        else next.set(k, v);
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
  };

  // 電台清單改用 /stations（分頁後當前頁湊不出完整電台清單）。
  useEffect(() => {
    document.title = '錄製節目';
    api.get('/stations')
      .then(res => {
        const data = res.data || [];
        setStationList(data.map(s => ({ id: s.station_id, name: s.station_name || s.station_id })));
      })
      .catch(err => console.error('取得電台清單錯誤:', err));
  }, []);

  // URL 的 kw 改變時（上一頁/下一頁、或分享連結開啟）同步回輸入框，避免輸入框
  // 與實際搜尋條件不一致。下方 debounce 有 keywordInput===searchKeyword 的防護，不會迴圈。
  useEffect(() => {
    setKeywordInput(searchKeyword);
  }, [searchKeyword]);

  // debounce 關鍵字輸入 → 寫進 URL
  useEffect(() => {
    if (keywordInput === searchKeyword) return;
    const t = setTimeout(() => updateParams({ kw: keywordInput }), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywordInput]);

  // 依 URL 參數抓該頁資料
  useEffect(() => {
    setLoading(true);
    api.get('/programs', {
      params: {
        status: 'recorded',
        page,
        page_size: PAGE_SIZE,
        ...(stationFilter && { station_id: stationFilter }),
        ...(searchKeyword && { keyword: searchKeyword }),
        ...(selectedDate && { date: selectedDate.replaceAll('-', '') }),
        ...(translatedOnly && { translated: 1 }),
      },
    })
      .then(res => {
        const data = res.data || {};
        setPrograms(data.items || []);
        setTotal(data.total || 0);
        setLoading(false);
      })
      .catch(err => {
        console.error('取得錄製節目錯誤:', err);
        setLoading(false);
      });
  }, [page, stationFilter, searchKeyword, selectedDate, translatedOnly]);

  const formatProgramTime = (timeString) => {
    if (!timeString || timeString.length < 12) return '時間未知';
    const year = timeString.slice(0, 4);
    const month = timeString.slice(4, 6);
    const day = timeString.slice(6, 8);
    const hour = timeString.slice(8, 10);
    const minute = timeString.slice(10, 12);
    return `${year}/${month}/${day} ${hour}:${minute}`;
  };

  const getDurationText = (start, end) => {
    const s = new Date(`${start.slice(0, 4)}-${start.slice(4, 6)}-${start.slice(6, 8)}T${start.slice(8, 10)}:${start.slice(10, 12)}:${start.slice(12, 14)}+09:00`);
    const e = new Date(`${end.slice(0, 4)}-${end.slice(4, 6)}-${end.slice(6, 8)}T${end.slice(8, 10)}:${end.slice(10, 12)}:${end.slice(12, 14)}+09:00`);
    const diffMin = Math.round((e - s) / 60000);
    const hours = Math.floor(diffMin / 60);
    const mins = diffMin % 60;
    return hours > 0 ? `${hours}小時${mins > 0 ? mins + '分鐘' : ''}` : `${mins}分鐘`;
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box>
      <FormControl fullWidth margin="normal">
        <InputLabel id="station-select-label">電台</InputLabel>
        <Select
          labelId="station-select-label"
          value={stationFilter}
          label="電台"
          onChange={(e) => updateParams({ station: e.target.value })}
          renderValue={(selected) => {
            if (!selected) return '全部';
            const match = stationList.find(s => s.id === selected);
            return match ? match.name : selected;
          }}
        >
          <MenuItem value="">全部</MenuItem>
          {stationList.map(station => (
            <MenuItem key={station.id} value={station.id}>{station.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        margin="normal"
        label="搜尋節目關鍵字"
        value={keywordInput}
        onChange={(e) => setKeywordInput(e.target.value)}
      />

      <TextField
        fullWidth
        margin="normal"
        type="date"
        label="日期"
        value={selectedDate}
        onChange={(e) => updateParams({ date: e.target.value })}
        slotProps={{
          inputLabel: { shrink: true },
          input: { notched: true },
        }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={translatedOnly}
            onChange={(e) => updateParams({ translated: e.target.checked ? '1' : '' })}
          />
        }
        label="已翻譯"
        sx={{ mt: 1 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : programs.length === 0 ? (
        <Typography sx={{ my: 4, textAlign: 'center' }} color="text.secondary">
          沒有符合條件的節目
        </Typography>
      ) : (
        <List>
          {programs.map((program) => (
            <ListItem key={program.id} disablePadding>
              <ListItemButton onClick={() => navigate(`/recorded/${program.id}`)}>
                <ListItemText
                  primary={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                      {program.title}
                      {program.translated && (
                        <Chip label="已翻譯" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                  }
                  secondary={`開始時間：${formatProgramTime(program.start_time)}｜頻道：${program.station_name || program.station_id}｜長度：${getDurationText(program.start_time, program.end_time)}`}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}

      <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, p) => updateParams({ page: p === 1 ? '' : String(p) }, { resetPage: false })}
          color="primary"
        />
      </Stack>
    </Box>
  );
}

export default RecordedByTimePage;
